import React, {useEffect, useState} from 'react';
import {XMarkIcon} from '@heroicons/react/24/outline';
import styles from './ExtendContractPopUp.module.css';
import Button from "../../../components/common/button/Button";
import {extendContractAPI} from "../../../services/admin";
import {useAlert} from "../../../components/common/error/AlertContext";

interface ExtendContractPopUpProps {
    isOpen: boolean;
    onClose: () => void;
    contractId?: number | null;
    currentExpireDate?: Date | null;
}
//계약 연장 팝업
const ExtendContractPopUp: React.FC<ExtendContractPopUpProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     contractId,
                                                                     currentExpireDate
                                                                 }) => {
    const [additionalPeriod, setAdditionalPeriod] = useState<number>(1);
    const [afterDate, setAfterDate] = useState<Date | null>(null);
    const [reason, setReason] = useState<string>('');
    const {showAlert} = useAlert();

    useEffect(() => {
        if (currentExpireDate) {
            const current = new Date(currentExpireDate);
            const extended = new Date(current);
            extended.setDate(current.getDate() + additionalPeriod);
            setAfterDate(extended);
        }
    }, [currentExpireDate, additionalPeriod]);
    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const handleSubmit = async () => {
        //대상 계약 식별자, 이전 만료 날짜, 연장 후 만료 날짜 모두 기입 시 연장 가능
        if (!contractId || !currentExpireDate || !afterDate) {
            console.error('Required data is missing');
            return;
        }

        try {
            //연장 API
            await extendContractAPI({
                contractId,
                extensionDuration: additionalPeriod,
                beforeDate: formatDate(new Date(currentExpireDate)),
                afterDate: formatDate(afterDate),
                reason
            });
            showAlert("계약이 연장되었습니다.", "info");
            onClose();
        } catch (error) {
            console.error('Error extending contract:', error);
        }
    };

    if (!isOpen || !contractId || !currentExpireDate) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>계약일 연장</h2>
                    {/*닫기 버튼*/}
                    <button onClick={onClose} className={styles.closeButton}>
                        <XMarkIcon className={styles.closeIcon}/>
                    </button>
                </div>

                <div className={styles.content}>
                    {/*안내 문구*/}
                    <p className={styles.description}>
                        개월 단위 연장이 아닌 일 단위 연장인 경우에 사용하세요
                    </p>
                    <p className={styles.description}>
                        (1회 최대 30일까지 연장 가능)
                    </p>

                    <div className={styles.formGroup}>
                        <label>추가 기간</label>
                        {/*연장일, 최소 1, 최대 30*/}
                        <input
                            type="number"
                            min="1"
                            max="30"
                            value={additionalPeriod === 0 ? '' : additionalPeriod}
                            onChange={(e) => setAdditionalPeriod(Math.min(30, Math.max(1, parseInt(e.target.value) || 1)))}
                            className={styles.inputPeriod}
                        />
                        <span className={styles.unit}>일</span>
                    </div>

                    <div className={styles.formGroup}>
                        <label>기존 만료 날짜</label>
                        {/*기존 만료 날짜 자동 기입*/}
                        <input
                            type="text"
                            value={currentExpireDate.toString().split('T')[0]}
                            disabled
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>변경 만료 날짜</label>
                        {/*변경 만료 날짜 자동 기입*/}
                        <input
                            type="text"
                            value={afterDate ? formatDateToString(afterDate) : ''}
                            disabled
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>연장 사유</label>
                        {/*연장 사유 기입*/}
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="연장 사유를 적어주세요"
                            className={styles.textarea}
                        />
                    </div>
                </div>

                <div className={styles.footer}>
                    {/*연장 버튼*/}
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!reason.trim() || !additionalPeriod}
                        className={styles.submitButton}
                    >
                        연장
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ExtendContractPopUp;